import { z } from 'zod';

// Chart tooltip formatting preferences
export const tooltipFormattingSchema = z.object({
  // Number formatting rules
  showLocalizedNumbers: z.boolean().default(true),
  decimalPlacesLessThanOne: z.number().int().min(0).max(10).default(2),
  decimalPlacesOneToThirty: z.number().int().min(0).max(10).default(1),
  decimalPlacesGreaterThanThirty: z.number().int().min(0).max(10).default(0),
  
  // Additional formatting options
  showThousandsSeparator: z.boolean().default(true),
  currencySymbol: z.string().optional(),
  percentagePrecision: z.number().int().min(0).max(10).default(1),
});

// Chart display preferences
export const chartDisplayPreferencesSchema = z.object({
  // Grid and visual preferences
  showGridLines: z.boolean().default(true),
  showLegend: z.boolean().default(true),
  showTooltips: z.boolean().default(true),
  
  // Animation preferences
  enableAnimations: z.boolean().default(true),
  animationDuration: z.number().int().min(0).max(5000).default(750),
  
  // Color and theming
  defaultColorPalette: z.string().default('fireforge'),
  chartBackground: z.enum(['transparent', 'white', 'card']).default('card'),
  
  // Interaction preferences
  enableHover: z.boolean().default(true),
  enableZoom: z.boolean().default(false),
  enablePan: z.boolean().default(false),
});

// Overall chart preferences combining all preferences
export const chartPreferencesSchema = z.object({
  tooltipFormatting: tooltipFormattingSchema.default({}),
  displayPreferences: chartDisplayPreferencesSchema.default({}),
  defaultPaletteId: z.string().optional(),
});

// Types
export type TooltipFormatting = z.infer<typeof tooltipFormattingSchema>;
export type ChartDisplayPreferences = z.infer<typeof chartDisplayPreferencesSchema>;
export type ChartPreferences = z.infer<typeof chartPreferencesSchema>;

// Default values for easy access
export const defaultChartPreferences: ChartPreferences = {
  tooltipFormatting: {
    showLocalizedNumbers: true,
    decimalPlacesLessThanOne: 2,
    decimalPlacesOneToThirty: 1,
    decimalPlacesGreaterThanThirty: 0,
    showThousandsSeparator: true,
    percentagePrecision: 1,
  },
  displayPreferences: {
    showGridLines: true,
    showLegend: true,
    showTooltips: true,
    enableAnimations: true,
    animationDuration: 750,
    defaultColorPalette: 'fireforge',
    chartBackground: 'card',
    enableHover: true,
    enableZoom: false,
    enablePan: false,
  },
  defaultPaletteId: 'fireforge',
}; 